/**
 * Low-entropy device string (ENG-34 / #216). Browser-only, no dependencies —
 * adding a fingerprinting library was explicitly out of scope (disk budget,
 * and a high-entropy fingerprint is a bigger privacy liability than the abuse
 * it would catch).
 *
 * What it uses: user-agent, screen geometry, colour depth, device pixel ratio,
 * IANA timezone, language, and hardware/memory hints. Together these identify
 * a *class* of device, not a person — a common laptop model on a common OS in
 * a common timezone collides with thousands of others, which is why the rules
 * that consume it only ever produce `challenge`, never `block`
 * (lib/abuse/identity-check.ts).
 *
 * What it deliberately does NOT use: canvas/WebGL/audio fingerprinting, font
 * enumeration, or any storage-backed id. Those raise the entropy far enough to
 * single out an individual, which would turn this into tracking.
 *
 * The string is hashed with the deployment salt before it is stored, so what
 * is persisted is not reversible even to this list of fields.
 */

/** Stable, ordered so the same browser always produces the same string. */
export function deviceFingerprint(): string | null {
	if (typeof window === "undefined" || typeof navigator === "undefined") return null;
	try {
		const nav = navigator as Navigator & { deviceMemory?: number };
		const parts = [
			nav.userAgent ?? "",
			nav.language ?? "",
			String(nav.hardwareConcurrency ?? ""),
			String(nav.deviceMemory ?? ""),
			`${window.screen?.width ?? ""}x${window.screen?.height ?? ""}`,
			String(window.screen?.colorDepth ?? ""),
			String(window.devicePixelRatio ?? ""),
			Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
		];
		const value = parts.join("|");
		// All-empty ⇒ nothing worth recording (a stripped headless environment).
		return parts.some((p) => p.length > 0) ? value.slice(0, 256) : null;
	} catch {
		return null;
	}
}
